import { Component, Director, director, NodeEventType, Node, TransformBit, _decorator, UITransform } from 'cc';
import { DEV } from 'cc/env';

const { ccclass, property, executeInEditMode } = _decorator;

@ccclass('BaseAutoHiding')
@executeInEditMode
export default abstract class BaseAutoHiding extends Component {
    @property({ tooltip: DEV && 'The tolerance of visibility check, in pixel. the larger the value, the larger the visible area' })
    public readonly tolerance = 0;

    @property({
        tooltip:
            DEV &&
            'The extra hide tolerance for hiding visible items, in pixel. The item will show with tolerance and hide after leaving tolerance + extraHideTolerance.',
    })
    public readonly extraHideTolerance = 0;

    @property({ tooltip: DEV && '使用透明度隐藏節點而不是active' })
    public readonly hideByOpacity = false;

    @property
    private _addtionalItemRoots: Node[] = [];

    private _visibleSectionDirty = false;

    /**
     * 用於增加節點且避免因序列化而修改到場景
     */
    protected get allItemRoots() {
        return [...this._addtionalItemRoots];
    }

    @property([Node])
    private get addtionalItemRoots() {
        return this._addtionalItemRoots;
    }
    private set addtionalItemRoots(value: Node[]) {
        if (this._addtionalItemRoots === value) {
            return;
        }
        if (this._addtionalItemRoots) {
            this._addtionalItemRoots.forEach((itemRoot) => this._unregisterContentEvents(itemRoot));
        }
        this._addtionalItemRoots = value;
        if (this._addtionalItemRoots) {
            this._addtionalItemRoots.forEach((itemRoot) => this._registerContentEvents(itemRoot));
        }
    }

    /**
     * 顯示區域的 UITransform，用於判別可見區域
     */
    protected abstract get viewUTransform(): UITransform;

    public onEnable() {
        director.on(Director.EVENT_AFTER_UPDATE, this._updateVisibleSection, this);
        this.viewUTransform.node.on(NodeEventType.SIZE_CHANGED, this._markVisibleSectionDirty, this);
        this.allItemRoots.forEach((itemRoot) => this._registerContentEvents(itemRoot));
        this._visibleSectionDirty = true;
    }

    public onDisable() {
        director.off(Director.EVENT_AFTER_UPDATE, this._updateVisibleSection, this);
        this.viewUTransform.node.off(NodeEventType.SIZE_CHANGED, this._markVisibleSectionDirty, this);
        this.allItemRoots.forEach((itemRoot) => this._unregisterContentEvents(itemRoot));
    }

    public updateVisibleSection() {
        this._updateVisibleSection(true);
    }

    private _registerContentEvents(itemRoot: Node) {
        if (!itemRoot) {
            return;
        }
        itemRoot.on(NodeEventType.TRANSFORM_CHANGED, this._onTransformDirty, this);
        itemRoot.on(NodeEventType.CHILD_ADDED, this._onChildAdded, this);
        itemRoot.on(NodeEventType.CHILD_REMOVED, this._onChildRemoved, this);
        itemRoot.children.map((node) => this._onChildAdded(node));
    }

    private _unregisterContentEvents(itemRoot: Node) {
        if (!itemRoot) {
            return;
        }
        itemRoot.off(NodeEventType.TRANSFORM_CHANGED, this._onTransformDirty, this);
        itemRoot.off(NodeEventType.CHILD_ADDED, this._onChildAdded, this);
        itemRoot.off(NodeEventType.CHILD_REMOVED, this._onChildRemoved, this);
        itemRoot.children.map((node) => this._onChildRemoved(node));
    }

    private _onChildAdded(child: Node) {
        child.on(NodeEventType.TRANSFORM_CHANGED, this._onTransformDirty, this);
    }

    private _onChildRemoved(child: Node) {
        child.off(NodeEventType.TRANSFORM_CHANGED, this._onTransformDirty, this);
    }

    private _onTransformDirty(transformBit: TransformBit) {
        if (transformBit === TransformBit.POSITION || transformBit === TransformBit.SCALE) {
            this._markVisibleSectionDirty();
        }
    }

    private _markVisibleSectionDirty() {
        this._visibleSectionDirty = true;
    }

    private _updateVisibleSection(force = false) {
        if (!this._visibleSectionDirty && !force) {
            return;
        }
        this._visibleSectionDirty = false;

        const visibleBoundingBox = this.viewUTransform.getWorldBoundingBox().clone();
        const hidingBoundingBox = this.viewUTransform.getWorldBoundingBox().clone();

        // Expand the bounding box to avoid flickering
        if (this.tolerance !== 0) {
            visibleBoundingBox.xMin -= this.tolerance;
            visibleBoundingBox.yMin -= this.tolerance;
            visibleBoundingBox.xMax += this.tolerance;
            visibleBoundingBox.yMax += this.tolerance;

            hidingBoundingBox.xMin -= this.tolerance;
            hidingBoundingBox.yMin -= this.tolerance;
            hidingBoundingBox.xMax += this.tolerance;
            hidingBoundingBox.yMax += this.tolerance;
        }
        if (this.extraHideTolerance !== 0) {
            hidingBoundingBox.xMin -= this.extraHideTolerance;
            hidingBoundingBox.yMin -= this.extraHideTolerance;
            hidingBoundingBox.xMax += this.extraHideTolerance;
            hidingBoundingBox.yMax += this.extraHideTolerance;
        }
        [...this.allItemRoots].forEach((itemRoot) => {
            itemRoot?.children.forEach((node) => {
                const nodeBoundingBox = node.transform.getWorldBoundingBox();
                const isVisible = this.hideByOpacity ? node.opacity > 0 : node.active;
                const visible = isVisible ? hidingBoundingBox.intersects(nodeBoundingBox) : visibleBoundingBox.intersects(nodeBoundingBox);
                if (this.hideByOpacity) {
                    node.opacity = visible ? 255 : 0;
                } else {
                    node.active = visible;
                }
            });
        });
    }
}
